/**
 * PageShell — shared dark-theme design system for all policy/info pages.
 * Exports: PageWrapper, DarkNav, DarkHero, GlassCard, DarkSection,
 *          DarkTOC, DarkBulletList, DarkInfoBox, DarkP, DarkRow,
 *          DarkSubHeading, DarkFooter, DarkTimeline
 */
import { useNavigate, useLocation } from "react-router-dom";
import { StoreBadges } from "./StoreBadges";

// ── CSS ──────────────────────────────────────────────────────────────────────
export const PAGE_CSS = `
  @keyframes dkOrb1 {
    0%,100% { transform:translate(0,0) scale(1); }
    33%      { transform:translate(28px,-18px) scale(1.04); }
    66%      { transform:translate(-18px,12px) scale(0.96); }
  }
  @keyframes dkOrb2 {
    0%,100% { transform:translate(0,0) scale(1); }
    33%      { transform:translate(-22px,14px) scale(1.03); }
    66%      { transform:translate(18px,-10px) scale(0.97); }
  }
  @keyframes dkOrb3 {
    0%,100% { transform:translate(0,0) scale(1); }
    50%      { transform:translate(14px,22px) scale(1.02); }
  }
  @keyframes dkFadeUp {
    from { opacity:0; transform:translateY(22px); filter:blur(5px); }
    to   { opacity:1; transform:translateY(0);    filter:blur(0px); }
  }
  .dk0 { animation: dkFadeUp 0.55s cubic-bezier(.23,1,.32,1) both; }
  .dk1 { animation: dkFadeUp 0.55s 0.10s cubic-bezier(.23,1,.32,1) both; }
  .dk2 { animation: dkFadeUp 0.55s 0.20s cubic-bezier(.23,1,.32,1) both; }
  .dk3 { animation: dkFadeUp 0.55s 0.32s cubic-bezier(.23,1,.32,1) both; }
  .dk4 { animation: dkFadeUp 0.55s 0.44s cubic-bezier(.23,1,.32,1) both; }
  @media (max-width: 860px) { .dk-toc { display:none !important; } }
  @media (min-width: 861px) { .dk-toc { display:block !important; } }
  .dk-toc { display:none; }

  /* Page nav links in header */
  .dk-page-nav { display:flex; align-items:center; gap:4px; overflow-x:auto; scrollbar-width:none; -ms-overflow-style:none; }
  .dk-page-nav::-webkit-scrollbar { display:none; }
  .dk-nav-link {
    display:inline-flex; align-items:center; gap:5px;
    padding:6px 12px; border-radius:8px; text-decoration:none;
    font-size:12.5px; font-weight:500; white-space:nowrap;
    color:rgba(255,255,255,0.55);
    border:1px solid transparent;
    transition:color 0.15s, background 0.15s, border-color 0.15s;
  }
  .dk-nav-link:hover {
    color:#fff;
    background:rgba(139,92,246,0.15);
    border-color:rgba(139,92,246,0.3);
  }
  @media (max-width:640px) {
    .dk-logo-text  { display:none !important; }
    .dk-nav-link   { padding:7px 9px; font-size:11.5px; min-height:40px; }
    .dk-link-full  { display:none !important; }
    .dk-link-short { display:inline !important; }
    .dk-nav-home   { padding:7px 12px !important; min-height:40px !important; }
    .dk-footer-top {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 14px !important;
    }
    .dk-footer-badges {
      align-items: flex-start !important;
      width: 100%;
    }
    .dk-footer-bottom {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 8px !important;
    }
  }
  @media (min-width:641px) {
    .dk-link-short { display:none !important; }
    .dk-link-full  { display:inline !important; }
  }
`;

// ── PageWrapper ───────────────────────────────────────────────────────────────
export function PageWrapper({ children }) {
  return (
    <>
      <style>{PAGE_CSS}</style>
      <div data-page-scroll="true" style={{
        position: "fixed", inset: 0, overflowY: "auto",
        background: "linear-gradient(160deg, #0F172A 0%, #1E1B4B 45%, #0F172A 100%)",
        fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI','Inter',sans-serif",
        color: "#E2E8F0",
      }}>
        {/* Ambient glow orbs */}
        <div aria-hidden="true" style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:0, overflow:"hidden" }}>
          <div style={{
            position:"absolute", top:"-8%", left:"8%",
            width:700, height:700, borderRadius:"50%",
            background:"radial-gradient(circle, rgba(99,102,241,0.13) 0%, transparent 68%)",
            animation:"dkOrb1 20s ease-in-out infinite",
          }} />
          <div style={{
            position:"absolute", top:"42%", right:"-6%",
            width:560, height:560, borderRadius:"50%",
            background:"radial-gradient(circle, rgba(139,92,246,0.10) 0%, transparent 68%)",
            animation:"dkOrb2 25s ease-in-out infinite",
          }} />
          <div style={{
            position:"absolute", bottom:"4%", left:"34%",
            width:460, height:460, borderRadius:"50%",
            background:"radial-gradient(circle, rgba(79,70,229,0.08) 0%, transparent 68%)",
            animation:"dkOrb3 18s ease-in-out infinite",
          }} />
        </div>
        <div style={{ position:"relative", zIndex:1 }}>{children}</div>
      </div>
    </>
  );
}

// ── Page nav links shown in DarkNav ──────────────────────────────────────────
const PAGE_LINKS = [
  { href:"/pricing", label:"Pricing",        short:"Pricing"  },
  { href:"/contact", label:"Contact Us",     short:"Contact"  },
  { href:"/terms",   label:"Terms",          short:"Terms"    },
  { href:"/privacy", label:"Privacy Policy", short:"Privacy"  },
  { href:"/refund",  label:"Refund Policy",  short:"Refund"   },
];

// ── DarkNav ───────────────────────────────────────────────────────────────────
export function DarkNav({ px = 40 }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const others = PAGE_LINKS.filter(p => p.href !== pathname);

  return (
    <nav style={{
      position:"sticky", top:0, zIndex:50,
      background:"rgba(10,14,40,0.88)",
      backdropFilter:"blur(22px)", WebkitBackdropFilter:"blur(22px)",
      borderBottom:"1px solid rgba(139,92,246,0.22)",
      paddingLeft:px, paddingRight:px,
      paddingTop:"var(--sat, 0px)",
      minHeight:62,
      display:"flex", alignItems:"center", gap:16,
    }}>
      {/* Logo */}
      <button
        onClick={() => navigate("/")}
        style={{ display:"flex", alignItems:"center", gap:10, background:"none", border:"none", cursor:"pointer", padding:0, flexShrink:0 }}
      >
        <div style={{
          width:34, height:34, borderRadius:10,
          background:"linear-gradient(135deg, #6366F1 0%, #7C3AED 100%)",
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 0 14px rgba(99,102,241,0.5)",
        }}>
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <path d="M8 2L14 5.5V10.5L8 14L2 10.5V5.5L8 2Z" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
            <circle cx="8" cy="8" r="2" fill="#fff"/>
          </svg>
        </div>
        <span className="dk-logo-text" style={{ fontSize:15, fontWeight:800, color:"#fff", letterSpacing:1.5 }}>LORRYKHOJO</span>
      </button>

      {/* Divider */}
      <div style={{ width:1, height:20, background:"rgba(139,92,246,0.3)", flexShrink:0 }} />

      {/* Other page links */}
      <div className="dk-page-nav" style={{ flex:1 }}>
        {others.map(p => (
          <a key={p.href} href={p.href} className="dk-nav-link">
            <span className="dk-link-short" style={{ display:"none" }}>{p.short}</span>
            <span className="dk-link-full">{p.label}</span>
          </a>
        ))}
      </div>

      {/* Home pill */}
      <button
        onClick={() => navigate("/")}
        className="dk-nav-home"
        style={{
          flexShrink:0, display:"flex", alignItems:"center", gap:6,
          background:"rgba(99,102,241,0.1)",
          border:"1px solid rgba(99,102,241,0.28)",
          borderRadius:8, color:"rgba(165,180,252,0.85)",
          cursor:"pointer", fontSize:12, fontWeight:600,
          padding:"6px 14px", transition:"all 0.15s", whiteSpace:"nowrap",
        }}
        onMouseEnter={e => { e.currentTarget.style.background="rgba(99,102,241,0.22)"; e.currentTarget.style.color="#fff"; }}
        onMouseLeave={e => { e.currentTarget.style.background="rgba(99,102,241,0.1)";  e.currentTarget.style.color="rgba(165,180,252,0.85)"; }}
      >
        ⌂ Home
      </button>
    </nav>
  );
}

// ── DarkHero ──────────────────────────────────────────────────────────────────
export function DarkHero({ badge, title, subtitle, px = 40, centered = false, meta }) {
  return (
    <div style={{
      padding:`60px ${px}px 48px`,
      textAlign: centered ? "center" : "left",
    }}>
      {badge && (
        <div className="dk0" style={{
          display:"inline-flex", alignItems:"center", gap:6,
          background:"rgba(99,102,241,0.15)",
          border:"1px solid rgba(99,102,241,0.32)",
          borderRadius:99, padding:"5px 14px",
          fontSize:11, fontWeight:700, color:"#A5B4FC",
          letterSpacing:1.2, textTransform:"uppercase",
          marginBottom:20,
        }}>
          {badge}
        </div>
      )}
      <h1 className="dk1" style={{
        fontSize:"clamp(28px,5vw,52px)",
        fontWeight:900, margin:"0 0 16px",
        background:"linear-gradient(135deg,#fff 0%,#A5B4FC 100%)",
        WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
        letterSpacing:-1, lineHeight:1.1,
      }}>
        {title}
      </h1>
      {subtitle && (
        <p className="dk2" style={{
          fontSize:"clamp(13px,1.7vw,16.5px)",
          color:"rgba(255,255,255,0.58)",
          margin:0, lineHeight:1.75,
          maxWidth: centered ? 560 : 540,
          marginLeft: centered ? "auto" : undefined,
          marginRight: centered ? "auto" : undefined,
        }}>
          {subtitle}
        </p>
      )}
      {meta && (
        <p className="dk3" style={{ fontSize:12.5, color:"rgba(165,180,252,0.5)", margin:"12px 0 0" }}>{meta}</p>
      )}
    </div>
  );
}

// ── GlassCard ─────────────────────────────────────────────────────────────────
export function GlassCard({ children, style }) {
  return (
    <div style={{
      background:"rgba(10,14,40,0.62)",
      backdropFilter:"blur(22px)", WebkitBackdropFilter:"blur(22px)",
      border:"1px solid rgba(139,92,246,0.22)",
      borderRadius:18,
      boxShadow:"0 8px 32px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.05)",
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── DarkSection ───────────────────────────────────────────────────────────────
export function DarkSection({ id, title, children }) {
  return (
    <section id={id} style={{ marginBottom:44, scrollMarginTop:82 }}>
      <h2 style={{
        fontSize:18.5, fontWeight:700,
        background:"linear-gradient(90deg,#fff 0%,#A5B4FC 100%)",
        WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
        margin:"0 0 16px", letterSpacing:-0.3,
        paddingBottom:12,
        borderBottom:"1px solid rgba(139,92,246,0.2)",
      }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

// ── DarkTOC ───────────────────────────────────────────────────────────────────
export function DarkTOC({ sections, scrollTo }) {
  return (
    <aside style={{ width:220, flexShrink:0, position:"sticky", top:82 }} className="dk-toc">
      <GlassCard style={{ overflow:"hidden" }}>
        <div style={{
          padding:"12px 18px", fontSize:10, fontWeight:700,
          letterSpacing:1.2, color:"rgba(165,180,252,0.65)",
          textTransform:"uppercase",
          borderBottom:"1px solid rgba(139,92,246,0.15)",
          background:"rgba(99,102,241,0.06)",
        }}>
          Contents
        </div>
        {sections.map(s => (
          <button key={s.id} onClick={() => scrollTo(s.id)} style={{
            display:"block", width:"100%", textAlign:"left",
            background:"none", border:"none",
            borderBottom:"1px solid rgba(255,255,255,0.04)",
            cursor:"pointer", padding:"9px 18px",
            fontSize:12.5, color:"rgba(255,255,255,0.52)", fontWeight:500,
            transition:"all 0.15s",
          }}
            onMouseEnter={e => { e.currentTarget.style.color="#A5B4FC"; e.currentTarget.style.background="rgba(99,102,241,0.12)"; }}
            onMouseLeave={e => { e.currentTarget.style.color="rgba(255,255,255,0.52)"; e.currentTarget.style.background="none"; }}
          >
            {s.label}
          </button>
        ))}
      </GlassCard>
    </aside>
  );
}

// ── DarkBulletList ────────────────────────────────────────────────────────────
export function DarkBulletList({ items }) {
  return (
    <ul style={{ margin:"10px 0 16px", paddingLeft:0, listStyle:"none", display:"flex", flexDirection:"column", gap:9 }}>
      {items.map((item, i) => (
        <li key={i} style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
          <span style={{
            marginTop:8, width:5, height:5, borderRadius:"50%", flexShrink:0,
            background:"linear-gradient(135deg,#6366F1,#8B5CF6)",
            boxShadow:"0 0 6px rgba(99,102,241,0.55)",
          }} />
          <span style={{ fontSize:14.5, color:"rgba(255,255,255,0.68)", lineHeight:1.85 }}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

// ── DarkInfoBox ───────────────────────────────────────────────────────────────
export function DarkInfoBox({ icon, children }) {
  return (
    <div style={{
      background:"rgba(99,102,241,0.09)",
      border:"1px solid rgba(99,102,241,0.32)",
      borderLeft:"3px solid #6366F1",
      borderRadius:"0 13px 13px 0",
      padding:"14px 18px", margin:"14px 0",
      fontSize:13.5, color:"rgba(165,180,252,0.92)", lineHeight:1.75,
      display:"flex", gap:10, alignItems:"flex-start",
    }}>
      {icon && <span style={{ fontSize:16, flexShrink:0, marginTop:1 }}>{icon}</span>}
      <span>{children}</span>
    </div>
  );
}

// ── DarkP ─────────────────────────────────────────────────────────────────────
export function DarkP({ children }) {
  return (
    <p style={{ fontSize:14.5, color:"rgba(255,255,255,0.65)", lineHeight:1.85, margin:"0 0 12px" }}>
      {children}
    </p>
  );
}

// ── DarkSubHeading ────────────────────────────────────────────────────────────
export function DarkSubHeading({ children }) {
  return (
    <div style={{
      fontSize:12, fontWeight:700, color:"#A5B4FC",
      textTransform:"uppercase", letterSpacing:1,
      margin:"18px 0 9px",
    }}>
      {children}
    </div>
  );
}

// ── DarkRow ───────────────────────────────────────────────────────────────────
export function DarkRow({ label, value, html, children }) {
  return (
    <div style={{
      display:"flex", gap:12, alignItems:"flex-start", fontSize:14,
      paddingBottom:10, borderBottom:"1px solid rgba(255,255,255,0.05)",
    }}>
      <span style={{ width:95, flexShrink:0, color:"rgba(165,180,252,0.7)", fontWeight:500, fontSize:13 }}>
        {label}
      </span>
      {children || (
        html
          ? <span style={{ color:"rgba(255,255,255,0.85)", fontWeight:500 }} dangerouslySetInnerHTML={{ __html: value }} />
          : <span style={{ color:"rgba(255,255,255,0.85)", fontWeight:500 }}>{value}</span>
      )}
    </div>
  );
}

// ── DarkTimeline ──────────────────────────────────────────────────────────────
export function DarkTimeline({ rows, col1Key = "step", col2Key = "desc", col1Width = 90 }) {
  return (
    <GlassCard style={{ overflow:"hidden", marginBottom:14 }}>
      {rows.map((row, i) => (
        <div key={i} style={{
          display:"flex", gap:16, padding:"13px 20px",
          background: i % 2 === 0 ? "rgba(99,102,241,0.05)" : "transparent",
          borderBottom: i < rows.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
          flexWrap:"wrap",
        }}>
          <span style={{ width:col1Width, flexShrink:0, fontSize:12.5, fontWeight:700, color:"#A5B4FC" }}>{row[col1Key]}</span>
          <span style={{ fontSize:13.5, color:"rgba(255,255,255,0.68)" }}>{row[col2Key]}</span>
        </div>
      ))}
    </GlassCard>
  );
}

// ── DarkContactRow ────────────────────────────────────────────────────────────
// contact card footer row (used only in Contact page's card)
export function DarkContactInfo({ rows }) {
  return (
    <GlassCard style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:12 }}>
      {rows.map((r, i) => <DarkRow key={i} {...r} />)}
    </GlassCard>
  );
}

// ── DarkFooter ────────────────────────────────────────────────────────────────
export function DarkFooter({ px = 40 }) {
  return (
    <div style={{
      borderTop:"1px solid rgba(139,92,246,0.2)",
      background:"rgba(10,14,40,0.85)",
      padding:`20px ${px}px`,
      paddingBottom:`max(20px, calc(20px + var(--sab, 0px)))`,
    }}>
      {/* top row — brand + store badges */}
      <div className="dk-footer-top" style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        flexWrap:"wrap", gap:16,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{
            width:26, height:26, borderRadius:7,
            background:"linear-gradient(135deg,#6366F1,#7C3AED)",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:9, fontWeight:900, color:"#fff",
            boxShadow:"0 0 10px rgba(99,102,241,0.4)",
          }}>LK</div>
          <span style={{ fontSize:13, fontWeight:800, color:"#fff", letterSpacing:1 }}>LORRYKHOJO</span>
        </div>

        <div className="dk-footer-badges" style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
          <span style={{ fontSize:10.5, fontWeight:700, color:"rgba(165,180,252,0.55)", letterSpacing:0.8, textTransform:"uppercase" }}>
            Get the app
          </span>
          <StoreBadges />
        </div>
      </div>

      <div style={{ height:1, background:"rgba(139,92,246,0.15)", margin:"18px 0 14px" }} />

      {/* bottom row — credit + contact */}
      <div className="dk-footer-bottom" style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        flexWrap:"wrap", gap:12,
      }}>
        <span style={{ fontSize:12, color:"rgba(255,255,255,0.32)" }}>
          Made with <span style={{ color:"#F87171" }}>❤</span> in India by{" "}
          <a href="https://gvalabs.in/" target="_blank" rel="noopener noreferrer"
            style={{ color:"rgba(165,180,252,0.6)", textDecoration:"none", fontWeight:600 }}
            onMouseEnter={e => e.currentTarget.style.color="#A5B4FC"}
            onMouseLeave={e => e.currentTarget.style.color="rgba(165,180,252,0.6)"}
          >GVALabs</a>
        </span>
        <a href="mailto:namaste@lorrykhojo.com" style={{
          fontSize:12, color:"#A5B4FC", textDecoration:"none", fontWeight:500,
        }}
          onMouseEnter={e => e.currentTarget.style.color="#fff"}
          onMouseLeave={e => e.currentTarget.style.color="#A5B4FC"}
        >
          namaste@lorrykhojo.com
        </a>
      </div>
    </div>
  );
}
